import React from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Modal from "components/ui/Modal";

// Sortable Placeholder Item Component
function SortablePlaceholderItem({
  field,
  index,
  register,
  errors,
  onRemove,
  canRemove,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-2 items-center ${isDragging ? "z-10" : ""}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-2 hover:bg-lightgray rounded"
        title="Drag to reorder"
      >
        <svg
          className="w-4 h-4 text-darkgray"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M3 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1z" />
        </svg>
      </div>
      {/* Input Field */}
      <input
        type="text"
        {...register(`placeholders.${index}.value`)}
        placeholder={`PLACEHOLDER_${index + 1} (e.g., FIRST_NAME, PROPERTY_ADDRESS)`}
        className={`form-input flex-1 border ${
          errors.placeholders?.[index]?.value
            ? "border-red-500"
            : "border-mediumgray"
        }`}
      />
      {/* Remove Button */}
      <button
        type="button"
        onClick={() => onRemove(index)}
        disabled={!canRemove}
        className={`px-3 py-2 rounded-md text-sm font-medium ${
          !canRemove
            ? "bg-lightgray text-darkgray cursor-not-allowed"
            : "bg-red-100 text-red-700 hover:bg-red-200"
        }`}
      >
        Remove
      </button>
    </div>
  );
}

// SMS Character Counter Component
function SmsCharacterCounter({ description, placeholders }) {
  const calculateSmsMetrics = () => {
    if (!description) {
      return {
        currentChars: 0,
        estimatedMinChars: 0,
        estimatedMaxChars: 0,
        minSmsCount: 0,
        maxSmsCount: 0,
      };
    }

    // Current character count (with placeholders as-is)
    const currentChars = description.length;

    // Estimated character counts
    let estimatedMinChars = description;
    let estimatedMaxChars = description;

    // Common placeholder value estimates
    const placeholderEstimates = {
      min: 5,   // e.g., "John"
      max: 30,  // e.g., "123 Main Street, Apartment 4B"
    };

    // Replace each placeholder with estimated values
    placeholders.forEach((placeholder) => {
      const placeholderName = placeholder.value?.trim();
      if (placeholderName) {
        const pattern = `{{${placeholderName}}}`;
        
        // Estimate min: replace with short value
        estimatedMinChars = estimatedMinChars.replace(
          new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'g'),
          'X'.repeat(placeholderEstimates.min)
        );
        
        // Estimate max: replace with long value
        estimatedMaxChars = estimatedMaxChars.replace(
          new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'g'),
          'X'.repeat(placeholderEstimates.max)
        );
      }
    });

    const minChars = estimatedMinChars.length;
    const maxChars = estimatedMaxChars.length;

    // Calculate SMS count (153 characters per SMS)
    const SMS_LIMIT = 153;
    const minSmsCount = Math.ceil(minChars / SMS_LIMIT);
    const maxSmsCount = Math.ceil(maxChars / SMS_LIMIT);

    return {
      currentChars,
      estimatedMinChars: minChars,
      estimatedMaxChars: maxChars,
      minSmsCount,
      maxSmsCount,
    };
  };

  const metrics = calculateSmsMetrics();

  // Color coding based on SMS count
  const getSmsCountColor = (count) => {
    if (count === 1) return "text-green-600";
    if (count === 2) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-md space-y-2">
      <div className="text-sm font-semibold text-gray-700">
        SMS Estimation
      </div>
      
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-gray-600">Current Characters:</div>
          <div className="font-medium">{metrics.currentChars}</div>
        </div>
        
        <div>
          <div className="text-gray-600">Estimated Range:</div>
          <div className="font-medium">
            {metrics.estimatedMinChars} - {metrics.estimatedMaxChars} chars
          </div>
        </div>
      </div>

      <div className="pt-2 border-t border-gray-300">
        <div className="text-gray-600 text-sm mb-1">SMS Count Estimate:</div>
        <div className="flex items-center gap-4">
          <div>
            <span className="text-xs text-gray-500">Min: </span>
            <span className={`font-bold text-lg ${getSmsCountColor(metrics.minSmsCount)}`}>
              {metrics.minSmsCount}
            </span>
            <span className="text-xs text-gray-500"> SMS</span>
          </div>
          <div className="text-gray-400">→</div>
          <div>
            <span className="text-xs text-gray-500">Max: </span>
            <span className={`font-bold text-lg ${getSmsCountColor(metrics.maxSmsCount)}`}>
              {metrics.maxSmsCount}
            </span>
            <span className="text-xs text-gray-500"> SMS</span>
          </div>
        </div>
      </div>

      <div className="pt-2 text-xs text-gray-500">
        * Estimates assume placeholder values between 5-30 characters
        <br />* Each SMS supports up to 153 characters
      </div>
    </div>
  );
}

export default function SmsTemplateEdit({
  initialData = {},
  onSubmit,
  existingTemplates = [],
}) {
  const [showCopyModal, setShowCopyModal] = React.useState(false);
  const [validationError, setValidationError] = React.useState("");
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData.name || "",
      description: initialData.description || "",
      placeholders:
        initialData.placeholders?.length > 0
          ? initialData.placeholders.map((placeholder) => ({
              value: placeholder,
            }))
          : [{ value: "" }],
    },
  });
  const {
    fields: placeholderFields,
    append: appendPlaceholder,
    remove: removePlaceholderField,
    move: movePlaceholder,
  } = useFieldArray({
    control,
    name: "placeholders",
  });
  
  // Watch description and placeholders for validation and character counting
  const description = watch("description");
  const placeholders = watch("placeholders");
  
  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );
  
  const validatePlaceholders = (data) => {
    const desc = data.description || "";
    const placeholderList = data.placeholders
      .map((p) => p.value)
      .filter((p) => p.trim() !== "");
    if (placeholderList.length === 0) {
      return true;
    }
    const missingPlaceholders = [];
    placeholderList.forEach((placeholder) => {
      const placeholderPattern = `{{${placeholder}}}`;
      if (!desc.includes(placeholderPattern)) {
        missingPlaceholders.push(placeholder);
      }
    });
    if (missingPlaceholders.length > 0) {
      setValidationError(
        `The following placeholders are not present in the description: ${missingPlaceholders
          .map((p) => `{{${p}}}`)
          .join(", ")}`
      );
      return false;
    }
    setValidationError("");
    return true;
  };
  
  const submitForm = (data) => {
    if (!validatePlaceholders(data)) {
      return;
    }
    const transformedData = {
      ...data,
      placeholders: data.placeholders
        .map((placeholder) => placeholder.value)
        .filter((p) => p.trim() !== ""),
    };
    onSubmit(transformedData);
  };
  
  const handlePlaceholderDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = placeholderFields.findIndex(
        (field) => field.id === active.id
      );
      const newIndex = placeholderFields.findIndex(
        (field) => field.id === over.id
      );
      movePlaceholder(oldIndex, newIndex);
    }
  };
  
  const addPlaceholder = () => {
    appendPlaceholder({ value: "" });
  };
  
  const removePlaceholder = (index) => {
    if (placeholderFields.length > 1) {
      removePlaceholderField(index);
    }
  };
  
  const copyFromTemplate = (templateId) => {
    const template = existingTemplates.find((t) => t._id === templateId);
    if (!template) return;
    if (template.placeholders && template.placeholders.length > 0) {
      const currentLength = placeholderFields.length;
      for (let i = currentLength - 1; i >= 0; i--) {
        removePlaceholderField(i);
      }
      template.placeholders.forEach((placeholder) => {
        appendPlaceholder({ value: placeholder });
      });
    }
    setShowCopyModal(false);
  };
  
  React.useEffect(() => {
    if (validationError) {
      setValidationError("");
    }
  }, [description, placeholders]);
  
  return (
    <>
      <form onSubmit={handleSubmit(submitForm)} className="space-y-4">
        {/* Template Name */}
        <div className="space-y-2">
          <label htmlFor="name" className="form-label">
            Template Name *
          </label>
          <input
            type="text"
            id="name"
            {...register("name", { required: "Template name is required" })}
            placeholder="e.g., Welcome Message, Appointment Reminder"
            className={`form-input border ${
              errors.name ? "border-red-500" : "border-mediumgray"
            }`}
          />
          {errors.name && (
            <p className="form-error-message">{errors.name.message}</p>
          )}
        </div>
        
        {/* Description */}
        <div className="space-y-2">
          <label htmlFor="description" className="form-label">
            Description *
          </label>
          <textarea
            id="description"
            {...register("description", {
              required: "Description is required",
            })}
            placeholder="Describe this SMS template and when it should be used..."
            rows={4}
            className={`form-textarea border ${
              errors.description || validationError
                ? "border-red-500"
                : "border-mediumgray"
            }`}
          />
          {errors.description && (
            <p className="form-error-message">{errors.description.message}</p>
          )}
          {validationError && (
            <p className="form-error-message">{validationError}</p>
          )}
          
          {/* SMS Character Counter */}
          <SmsCharacterCounter 
            description={description} 
            placeholders={placeholders}
          />
        </div>
        
        {/* Placeholders with Drag & Drop */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="form-label">
              Placeholders{" "}
              <span className="text-sm font-normal text-darkgray">
                (Variables that will be replaced with actual values)
              </span>
            </label>
            {existingTemplates.length > 0 && (
              <button
                type="button"
                onClick={() => setShowCopyModal(true)}
                className="gray-button"
              >
                Copy from Template
              </button>
            )}
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handlePlaceholderDragEnd}
          >
            <SortableContext
              items={placeholderFields.map((field) => field.id)}
              strategy={rectSortingStrategy}
            >
              <div className="space-y-3">
                {placeholderFields.map((field, index) => (
                  <SortablePlaceholderItem
                    key={field.id}
                    field={field}
                    index={index}
                    register={register}
                    errors={errors}
                    onRemove={removePlaceholder}
                    canRemove={placeholderFields.length > 1}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <button
            type="button"
            onClick={addPlaceholder}
            className="mt-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-200"
          >
            Add Placeholder
          </button>
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Use uppercase with underscores for placeholders. They will be wrapped in double curly braces, e.g.,{" "}
              <code className="bg-white px-1 py-0.5 rounded">
                {"{{FIRST_NAME}}"}
              </code>
              ,{" "}
              <code className="bg-white px-1 py-0.5 rounded">
                {"{{PROPERTY_ADDRESS}}"}
              </code>
              . <strong>All placeholders must be present in the description.</strong>
            </p>
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="flex justify-end mt-6">
          <button type="submit" className="black-button">
            {initialData._id ? "Update Template" : "Create Template"}
          </button>
        </div>
      </form>
      
      <Modal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        title="Copy placeholders from existing template"
      >
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {existingTemplates.map((template) => (
            <button
              key={template._id}
              type="button"
              onClick={() => copyFromTemplate(template._id)}
              className="w-full text-left p-3 border border-mediumgray rounded-lg hover:bg-lightergray transition-colors"
            >
              <div className="font-medium">{template.name}</div>
              <div className="text-xs text-darkergray mt-1">
                {template.description}
              </div>
              {template.placeholders && template.placeholders.length > 0 && (
                <div className="text-sm text-darkergray mt-2">
                  {template.placeholders.length} placeholder
                  {template.placeholders.length > 1 ? "s" : ""} •{" "}
                  {template.placeholders.slice(0, 3).join(", ")}
                  {template.placeholders.length > 3 && "..."}
                </div>
              )}
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}